import { Gender, Mate, Visibility } from "@/types/entity";
import DEFAULT_AVATAR from "@/assets/img/avatar.svg";
import { MediaUploadFileItem } from "@/types/component";
import { inApp, isImage } from "@/modules/common";
import { ref } from "vue";
import { MateType } from "@/enums/mateEnums";
import i18n from "@/locales";
function useName(name?: string) {
  return name || i18n.global.t(`User.mate.unnamed`);
}

function useMateTypeTextByMate(mate: Mate) {
  const { type } = mate;
  return useMateTypeText(type);
}

function useMateTypeText(type: MateType) {
  if (!type) return "";
  return i18n.global.t(`MateType.${type}`);
}

function useAllMateType(): Array<{ text: string; value: MateType }> {
  return [
    { text: i18n.global.t("MateType.original"), value: MateType.Original },
    { text: i18n.global.t("MateType.fan"), value: MateType.Fanswork },
  ];
}

function useLanguages(): Array<{ text: string; value: number }> {
  return [
    { text: "English", value: 1 },
    { text: "中文(简体)", value: 2 },
  ];
}

function useGender(gender: Gender): string | undefined {
  const genderMap = useAllGender();
  for (const [, { text, value }] of genderMap.entries()) {
    if (gender === value) {
      return text;
    }
  }
}

function useAllGender(): Array<{ text: string; value: Gender }> {
  return [
    { text: i18n.global.t("Newmates.notSet"), value: 0 },
    { text: i18n.global.t("Newmates.male"), value: 1 },
    { text: i18n.global.t("Newmates.female"), value: 2 },
    { text: i18n.global.t("Newmates.unisex"), value: 3 },
  ];
}

function useAllVisibility(): {
  text: string;
  desc?: string;
  value: Visibility;
}[] {
  // 1-可见; 2-仅自己可见; 3-仅关注者可见; 4-仅守护者可见; 5-隐藏
  return [
    {
      text: i18n.global.t("Visibilities.private"),
      desc: i18n.global.t("Visibilities.privateDesc"),
      value: 2,
    },
    // {
    //   text: t("Visibilities.onlyFollower"),
    //   desc: t("Visibilities.onlyFollowerDesc"),
    //   value: 3,
    // },
    {
      text: i18n.global.t("Visibilities.onlyAdmire"),
      desc: i18n.global.t("Visibilities.onlyAdmireDesc"),
      value: 4,
    },
    {
      text: i18n.global.t("Visibilities.public"),
      desc: i18n.global.t("Visibilities.publicDesc"),
      value: 1,
    },
  ];
}

function useDefaultAvatar(): string {
  return DEFAULT_AVATAR;
}

const isGif = (url: string): boolean => {
  return url.endsWith(".gif");
};

const isGifMedia = (mediaItem: MediaUploadFileItem) => {
  if (!mediaItem) return false;
  return (
    mediaItem.file?.type === "image/gif" || mediaItem.url?.endsWith(".gif")
  );
};

function usePreviewImg(url: string): string {
  if (url.endsWith(".webp")) return url;
  if (url.startsWith("data") || isGif(url)) return url;
  if (url.includes("/public")) return url.replace("/public", "/thumb");
  if (url.includes("/raw")) return url.replace("/raw", "/thumb");
  return url.includes("?") ? url : `${url}?x-oss-process=style/preview`;
}

function useAvatarImg(url: string): string {
  if (isGif(url)) return url;
  if (url.includes("/public")) return url.replace("/public", "/thumb");
  if (url.includes("/raw")) return url.replace("/raw", "/thumb");
  return `${url}?x-oss-process=style/preview`;
}

function useImgWithMediumSize(url: string, width = 571): string {
  if (!url) return "";
  if (!isImage(url)) return url;
  return `${url}?x-oss-process=image/resize,w_${width}/format,webp${
    isGif(url) ? "/quality,q_80" : ""
  }`;
}

function useVisibilityText(visibility: Visibility) {
  const visibilityMap = useAllVisibility();
  for (const [, { text, value }] of visibilityMap.entries()) {
    if (visibility === value) {
      return text;
    }
  }
}

function useVh() {
  // 处理移动端浏览器vh的问题
  // 1.根据innerHeight计算vh
  const vh = ref<number>(0);
  const updateVh = () => {
    if (vh.value && vh.value === window.innerHeight * 0.01) return;
    vh.value = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh.value}px`);
  };
  updateVh();
  // 2. 监听resize事件，重新计算vh
  window.addEventListener("resize", () => {
    updateVh();
    // 2.1 兼容 innerHeight 更新延迟
    setTimeout(updateVh, 100);
  });
}

const useNodeTypeText = (type: GalGameNode["type"]) => {
  switch (type) {
    case 1:
      return i18n.global.t("game.beginning");
    case 3:
      return i18n.global.t("game.tab.endings");
    case 2:
    default:
      return i18n.global.t("game.plotNode");
  }
};

const useCharacterName = (character: GalGameCharacter) => {
  if (character.name) return character.name;
  return i18n.global.t("game.role", character.role!);
};

const useStaticUrl = (url: string) => {
  return import.meta.env.VITE_STATIC_URL_PREFIX + "static" + url;
};

const useUserPromptUrl = (id: number) => {
  return (
    import.meta.env.VITE_STATIC_URL_PREFIX + `user_prompt/${id}/config.json`
  );
};

const useCrystalUrl = () => `/img/pay/crystal.webp`;
const useGalGameOrigin = () => {
  if (import.meta.env.VITE_GAL_GAME_URL) {
    return import.meta.env.VITE_GAL_GAME_URL;
  } else {
    return "";
  }
};

const host = "https://www.any-mate.com";
const useMateHomeUrl = (uuid: string) => `${host}/mate/${uuid}`;
const useTextImageUrl = (uuid) => `${host}/text-image-explore?uuid=${uuid}`;

const usePowerUrl = () => `/img/pay/power-cube.webp`;

const useEnergyBoxUrl = () => `/img/energy-box-3.png`;

const useShortCount = (count: number) => {
  if (count < 1000) return count;
  // 保留两位小数
  if (count < 1000000) {
    // 1.2k
    return (count / 1000).toFixed(1) + "k";
  }
  // 1.2m
  return (count / 1000000).toFixed(1) + "m";
};

const useRawImageUrl = (url: string) =>
  url.replace("/public", "/raw").replace("/thumb", "/raw");

const usePublicImageUrl = (url: string) => {
  if (url.includes("/public")) return url;
  if (url.includes("/raw") || url.includes("/thumb"))
    return url.replace("/raw", "/public").replace("/thumb", "/public");
  return url.includes("?") ? url : `${url}?x-oss-process=style/preview`;
};

const useDefaultWorldviewImg = () => "/img/default-planet.gif";

const useSpriteSheetUrlPrefix = "https://www.bingciyuan.top/avatar-editor";
const useGifToJpg = (url: string) => {
  return url + `?x-oss-process=image/format,jpg/quality,q_80`;
};

const useImageInstance = () => {
  const image = new Image();
  image.crossOrigin = "anonymous";
  return image;
};

const useSpriteSheetUrl = (
  url: string,
  animation: string,
  variant: string = ""
) => {
  let animationPath = `${animation}/`;
  if (
    url.indexOf("/rod") > -1 ||
    url.indexOf("/whip") > -1 ||
    url.indexOf(animationPath) > -1
  ) {
    animationPath = "";
  }
  let variantPath = "";
  if (typeof variant === "string") {
    variantPath = variant?.split(" ").join("_") ?? "";
  }
  return `${useSpriteSheetUrlPrefix}/${url}${animationPath}${variantPath}.png?x-oss-process=image/format,webp`
    .replace("${head}", "male")
    .replace("${expression}", "default");
};

/**
 * 获取app版本（从user-agent中获取）
 * returns:
 * - 若不在app中，则返回null
 * - 若在app中，则返回{ version: string, buildNumber: string }
 * @returns {null | { version?: string; buildNumber?: string; }}
 */
const useAppVersion = (): null | {
  version?: string;
  buildNumber?: string;
} => {
  if (!inApp) return null;
  // 1. 获取user-agent
  const userAgent = navigator.userAgent;
  // 2. 获取app版本，格式为：Bingciyuan/${version}/${buildNumber}
  const version = userAgent.match(/Bingciyuan\/(\d+\.\d+\.\d+)\/(\d+)/);
  return {
    version: version ? version[1] : "1.0.0",
    buildNumber: version ? version[2] : "1",
  };
};

const useStorageSize = (size: number) => {
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)}MB`;
  return `${(size / 1024 / 1024 / 1024).toFixed(1)}GB`;
};

export {
  useMateTypeText,
  useMateTypeTextByMate,
  useAllMateType,
  useAllGender,
  useGender,
  useDefaultAvatar,
  useAllVisibility,
  usePreviewImg,
  useAvatarImg,
  useImgWithMediumSize,
  useVisibilityText,
  isGifMedia,
  useLanguages,
  useVh,
  useNodeTypeText,
  useStaticUrl,
  useUserPromptUrl,
  useCrystalUrl,
  useGalGameOrigin,
  usePowerUrl,
  useMateHomeUrl,
  useShortCount,
  useTextImageUrl,
  useRawImageUrl,
  usePublicImageUrl,
  useDefaultWorldviewImg,
  useSpriteSheetUrlPrefix,
  useGifToJpg,
  useImageInstance,
  useSpriteSheetUrl,
  useName,
  useAppVersion,
  useEnergyBoxUrl,
  useStorageSize,
};
